import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import {
  GetSessionSummaryResponse,
  SessionServiceFactory,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectRouteParams } from '../external-selectors';

/**
 * Factory that creates a reusable NgRx functional effect loading the session summary for a
 * cold-load landing page (e.g. Wallet's "Back" button) directly from SessionServiceFactory —
 * not by dispatching the shared SessionActions.GetSessionSummaryAction — so this cold-load path
 * can't cause side effects in the live checkout flow. tenantId/sessionId come from the current
 * route params, since no prior state can be assumed on a cold load.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiReceiptLandingSessionEffect = createReceiptLandingSessionEffect({
 *     initializedAction: PoiPaymentReceiptComponentActions.initialized,
 *     loadSessionSummarySuccessAction: PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *     loadSessionSummaryErrorAction: PoiPaymentReceiptComponentActions.loadSessionSummaryError,
 *   });
 */
export function createReceiptLandingSessionEffect<A extends Action<string>>(config: {
  initializedAction: ActionCreator<string, Creator<any[], A>>;
  loadSessionSummarySuccessAction: ActionCreator<
    string,
    Creator<[{ payload: GetSessionSummaryResponse }], Action>
  >;
  loadSessionSummaryErrorAction: ActionCreator<
    string,
    Creator<[{ error: HttpErrorResponse }], Action>
  >;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      sessionServiceFactory = inject(SessionServiceFactory),
    ) =>
      actions$.pipe(
        ofType(config.initializedAction),
        concatLatestFrom(() => store.select(selectRouteParams)),
        exhaustMap(([, params]) =>
          sessionServiceFactory
            .create()
            .getSessionSummaryResponseV1(params.tenantId, params.sessionId)
            .pipe(
              map(
                (response) =>
                  config.loadSessionSummarySuccessAction({
                    payload: response,
                  }) as Action,
              ),
              catchError((error: HttpErrorResponse) =>
                of(config.loadSessionSummaryErrorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
